import { Col, Flex, Row, Typography } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import Select from "../../../../../../Components/Select"
import Icon from "../../../../../../Icon"
import { ruleConditionsSummary } from "../../utils/ruleSummary"

import dim from "../dimmed.module.less"
import tableStyles from "../rulesTable.module.less"
import styles from "./assignmentRules.module.less"

const { Text } = Typography

// Assignment: one place to route disputes to the team. A default assignee set (shared
// round-robin for anything not matched; the account owner when nobody is picked) plus
// per-condition rules for specific cases. Laid out like every other Preferences
// section: title + description on the left, controls on the right, no card frame.
const AssignmentRules = ({
  rules = [],
  members = [],
  defaultAssignees = [],
  onChangeDefault,
  onInvite,
  locked,
  onAdd,
  onEdit,
  onRemove
}) => {
  const { t } = useTranslation()
  const memberName = (id) => (members.find((m) => m.id === id) || {}).name || t("Unassigned")
  // Rules saved before the Type field existed carry no `types` and mean both.
  const typesLabel = (types) =>
    !Array.isArray(types) || types.length !== 1
      ? t("Disputes & inquiries")
      : types[0] === "inquiry"
        ? t("Inquiries")
        : t("Disputes")

  return (
    <Row gutter={[24, 24]} className={styles.assign}>
      <Col flex="1 1 300px">
        <Flex vertical gap={8} style={{ maxWidth: 500 }}>
          <Text strong>{t("Assignment")}</Text>
          <Text type="secondary">
            {t(
              "Decide who takes ownership of each dispute. Pick a default assignee, then add rules only if some cases should go to someone else."
            )}
          </Text>
          {/* The most common misreading of this section is that assigning restricts who
              can see a dispute. It does not, so say so up front (one line, not a
              callout box: the column is already dense). */}
          <Text strong>
            {t(
              "Everyone on your team sees every dispute. Assigning only sets who is emailed and shown as the owner."
            )}
          </Text>
        </Flex>
      </Col>

      <Col flex="0 1 420px">
        <Flex vertical gap={16}>
          <Flex vertical gap={8} className={locked ? dim.dimmed : ""}>
            <Text>{t("Default assignee")}</Text>
            <Select
              size="large"
              value={defaultAssignees[0]}
              placeholder={t("Choose a team member")}
              options={members.map((m) => ({ value: m.id, label: m.name }))}
              // Stored as an array for backward compatibility; the UI allows one person.
              onChange={(v) => onChangeDefault(v ? [v] : [])}
              onClear={() => onChangeDefault([])}
              allowClear
              notFoundContent={t("No team members yet")}
              disabled={locked}
              style={{ width: "100%" }}
              // "Invite user" sits as the last row of the dropdown, but it's an action,
              // not a selectable assignee: onMouseDown prevents the option-select/blur so
              // it never becomes the field's value, and it opens the invite modal instead.
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div
                    className={styles.inviteRow}
                    role="button"
                    tabIndex={0}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onInvite}
                  >
                    <Icon type="plus" />
                    <span>{t("Invite user")}</span>
                  </div>
                </>
              )}
            />
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t(
                "New disputes are assigned to this person and they get an email. If nobody is selected, they go to the account owner."
              )}
            </Text>
          </Flex>

          {rules.length === 0 ? null : (
            <div className={tableStyles.table}>
              {rules.map((rule) => (
                <div key={rule.id} className={`${tableStyles.row} ${tableStyles.rowData}`}>
                  <span className={tableStyles.cWhen}>
                    {typesLabel(rule.types)} · {ruleConditionsSummary(rule.conditions, t)}
                  </span>
                  <span>{memberName(rule.assignee)}</span>
                  <span className={tableStyles.cOps}>
                    <Button
                      type="text"
                      size="small"
                      aria-label={t("Edit rule")}
                      disabled={locked}
                      icon={<Icon type="edit_small" />}
                      onClick={() => onEdit(rule.id)}
                    />
                    <Button
                      type="text"
                      size="small"
                      danger
                      aria-label={t("Remove rule")}
                      disabled={locked}
                      icon={<Icon type="delete" />}
                      onClick={() => onRemove(rule.id)}
                    />
                  </span>
                </div>
              ))}
            </div>
          )}

          <Flex align="center" gap={12} wrap="wrap">
            <Button
              type="default"
              disabled={locked}
              icon={<Icon type="plus" />}
              onClick={onAdd}
              text={t("Add rule")}
            />
          </Flex>
        </Flex>
      </Col>
    </Row>
  )
}

export default AssignmentRules
